// src/services/trackingService.js
const API_BASE_URL = process.env.REACT_APP_MESSAGE_HANDLER_URL;

class TrackingService {
  constructor() {
    this.VISITOR_COOKIE_NAME = 'vid';
    this.SESSION_COOKIE_NAME = 'sid';
    this.VISITOR_MAX_AGE_DAYS = 400;

    this.sessionId = this.getOrCreateSessionId();
    this.visitorId = this.getOrCreateVisitorId();

    console.log('TrackingService initialized');
    console.log('Session ID:', this.sessionId);
    console.log('Visitor ID:', this.visitorId);

    // Sync with cookies
    this.syncWithCookies();
  }

  /**
   * Get cookie value by name
   */
  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop().split(';').shift();
    }
    return null;
  }

  /**
   * Set cookie with proper attributes
   */
  setCookie(name, value, days = null) {
    let expires = '';
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      expires = `; expires=${date.toUTCString()}`;
    }

    // Use Secure only in production (HTTPS)
    const isProduction = window.location.protocol === 'https:';
    const secureFlag = isProduction ? '; Secure' : '';

    document.cookie = `${name}=${value}${expires}; path=/; SameSite=Lax${secureFlag}`;
  }

  /**
   * Sync localStorage/sessionStorage with cookies
   */
  syncWithCookies() {
    // Set visitor ID cookie (long-lived)
    this.setCookie(this.VISITOR_COOKIE_NAME, this.visitorId, this.VISITOR_MAX_AGE_DAYS);

    // Set session ID cookie (session only - expires when browser closes)
    this.setCookie(this.SESSION_COOKIE_NAME, this.sessionId);

    console.log('✓ Synced tracking IDs with cookies');
  }

  /**
   * Get or create a session ID
   * Session ID should be regenerated on page refresh/reload
   */
  getOrCreateSessionId() {
    // Generate new session ID on every page load (refresh creates new session)
    const newSessionId = this.generateUUID();
    sessionStorage.setItem('session_id', newSessionId);
    console.log('New session created:', newSessionId);
    return newSessionId;
  }

  /**
   * Get or create visitor ID (persists across sessions)
   */
  getOrCreateVisitorId() {
    // Priority: localStorage > cookie > generate new
    let visitorId = localStorage.getItem('visitor_id');

    // Fallback to cookie if localStorage is empty
    if (!visitorId) {
      visitorId = this.getCookie(this.VISITOR_COOKIE_NAME);
      if (visitorId) {
        // Restore to localStorage
        localStorage.setItem('visitor_id', visitorId);
        console.log('Restored visitor ID from cookie:', visitorId);
      }
    }

    // Generate new if still not found
    if (!visitorId) {
      visitorId = this.generateUUID();
      localStorage.setItem('visitor_id', visitorId);
      console.log('New visitor ID created:', visitorId);
    } else {
      console.log('Existing visitor ID found:', visitorId);
    }

    return visitorId;
  }

  generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  getSessionId() {
    return this.sessionId;
  }

  getVisitorId() {
    return this.visitorId;
  }

  /**
   * Get tracking headers for API requests
   * USE THIS METHOD in your API calls
   */
  getTrackingHeaders() {
    return {
      'X-Visitor-ID': this.visitorId,
      'X-Session-ID': this.sessionId
    };
  }

  /**
   * Get tracking IDs as object
   */
  getTrackingIds() {
    return {
      visitorId: this.visitorId,
      sessionId: this.sessionId
    };
  }

  /**
   * Track page visit for single product page
   */
  async trackPageVisit(phoneNumber, pageId, keywords) {
    try {
      console.log('Tracking page visit:', {
        phoneNumber,
        pageId,
        keywords,
        sessionId: this.sessionId,
        visitorId: this.visitorId
      });

      const response = await fetch(`${API_BASE_URL}/api/track/page-visit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone_number: phoneNumber,
          visitor_id: this.visitorId,
          session_id: this.sessionId,
          page_id: pageId,
          keywords: keywords,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to track page visit:', response.status, errorText);
        return null;
      }

      const data = await response.json();
      console.log('Page visit tracked successfully:', data);
      return data;
    } catch (error) {
      console.error('Error tracking page visit:', error);
      return null;
    }
  }

  /**
   * Track product click
   */
  async trackProductClick(phoneNumber, productData) {
    try {
      console.log('Tracking product click:', {
        phoneNumber,
        productData,
        sessionId: this.sessionId,
        visitorId: this.visitorId
      });

      const response = await fetch(`${API_BASE_URL}/api/track/product-click`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone_number: phoneNumber,
          visitor_id: this.visitorId,
          session_id: this.sessionId,
          product_data: productData,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to track product click:', response.status, errorText);
        return null;
      }

      const data = await response.json();
      console.log('Product click tracked successfully:', data);
      return data;
    } catch (error) {
      console.error('Error tracking product click:', error);
      return null;
    }
  }

  async trackProductQuickViewClick(phoneNumber, productData) {
    try {
      console.log('Tracking product quick view click:', {
        phoneNumber,
        productData,
        sessionId: this.sessionId,
        visitorId: this.visitorId
      });

      const response = await fetch(`${API_BASE_URL}/api/track/quick-view-click`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone_number: phoneNumber,
          visitor_id: this.visitorId,
          session_id: this.sessionId,
          product_data: productData,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to track product click:', response.status, errorText);
        return null;
      }

      const data = await response.json();
      console.log('Product click tracked successfully:', data);
      return data;
    } catch (error) {
      console.error('Error tracking product click:', error);
      return null;
    }
  }

  /**
   * Track search activity
   */
  async trackSearchActivity(phoneNumber, searchType, searchData) {
    try {
      console.log('Tracking search activity:', {
        phoneNumber,
        searchType,
        searchData,
        sessionId: this.sessionId,
        visitorId: this.visitorId
      });

      const response = await fetch(`${API_BASE_URL}/api/track/search-activity`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone_number: phoneNumber,
          visitor_id: this.visitorId,
          session_id: this.sessionId,
          search_type: searchType,
          search_data: searchData,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to track search activity:', response.status, errorText);
        return null;
      }

      const data = await response.json();
      console.log('Search activity tracked successfully:', data);
      return data;
    } catch (error) {
      console.error('Error tracking search activity:', error);
      return null;
    }
  }

  /**
   * Get session summary (for debugging)
   */
  async getSessionSummary(phoneNumber) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/analytics/session-detail/${phoneNumber}/${this.visitorId}/${this.sessionId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting session summary:', error);
      return null;
    }
  }

  /**
   * Get all sessions for current visitor (for debugging)
   */
  async getVisitorSessions() {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/analytics/visitor-sessions/${this.visitorId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting visitor sessions:', error);
      return null;
    }
  }

  /**
   * Clear tracking data (for testing/debugging)
   */
  clearTracking() {
    localStorage.removeItem('visitor_id');
    sessionStorage.removeItem('session_id');
    this.setCookie(this.VISITOR_COOKIE_NAME, '', -1);
    this.setCookie(this.SESSION_COOKIE_NAME, '', -1);
    console.log('🗑️ Tracking data cleared');
  }
}

// Create and export singleton instance
const trackingService = new TrackingService();

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  window.trackingService = trackingService;
}

export default trackingService;
export { trackingService };